import { db } from './config'; // Removed storage import
import {
  collection,
  getDocs,
  doc,
  getDoc,
  updateDoc,
  deleteDoc,
  setDoc,
  where,
  query,
  orderBy
} from 'firebase/firestore';
// import { ref, getDownloadURL, deleteObject } from 'firebase/storage'; // Firebase storage functions no longer needed here for R2
import { deleteR2Object, getR2PublicUrl } from '../services/r2Service';

// Admin Operations
export const adminFunctions = {
  // Media Management
  getAllMedia: async () => {
    try {
      // Query all media from site_media collection, ordered by upload date
      const mediaRef = collection(db, 'site_media');
      const mediaQuery = query(mediaRef, orderBy('uploadedAt', 'desc'));
      const snapshot = await getDocs(mediaQuery);

      // Get unique site IDs from media
      const siteIds = [...new Set(snapshot.docs.map(doc => doc.data().siteId))];
      
      // Fetch site data in parallel
      const siteData = {};
      if (siteIds.length > 0) {
        const sitePromises = siteIds.map(async (siteId) => {
          const site = await adminFunctions.getSite(siteId);
          if (site) {
            siteData[siteId] = site;
          }
        });
        await Promise.all(sitePromises);
      }

      // Map media with site data
      return snapshot.docs.map(doc => {
        const data = doc.data();
        const site = siteData[data.siteId] || {};

        // Convert Firestore timestamps to dates
        const uploadedAt = data.uploadedAt?.toDate?.() || new Date(data.uploadedAt);
        const updatedAt = data.updatedAt?.toDate?.() || 
          (data.updatedAt ? new Date(data.updatedAt) : null);

        return {
          id: doc.id,
          ...data,
          uploadedAt,
          updatedAt,
          site: site || null,
          width: data.width, // Include width
          height: data.height // Include height
        };
      });
    } catch (error) {
      console.error('Error fetching media:', error);
      throw error;
    }
  },

  updateMedia: async (mediaId, updates) => {
    try {
      const mediaRef = doc(db, 'site_media', mediaId);
      await updateDoc(mediaRef, {
        ...updates,
        updatedAt: new Date()
      });
    } catch (error) {
      console.error('Error updating media:', error);
      throw error;
    }
  },

  deleteMedia: async (mediaId) => {
    try {
      const mediaRef = doc(db, 'site_media', mediaId);
      const mediaDoc = await getDoc(mediaRef);
      
      if (!mediaDoc.exists()) {
        throw new Error('Media not found in Firestore');
      }
      
      const mediaData = mediaDoc.data();
      
      // mediaData.path should now store the R2 objectKey
      if (mediaData.path) {
        try {
          await deleteR2Object(mediaData.path);
          console.log('File deleted from R2:', mediaData.path);
        } catch (r2Error) {
          console.error('Error deleting file from R2:', r2Error);
          // Decide if you want to proceed with Firestore deletion if R2 deletion fails.
          // For now, we'll log and continue, but this might need review.
          // throw new Error(`Failed to delete R2 object ${mediaData.path}: ${r2Error.message}`);
        }
      } else {
        console.warn(`Media document ${mediaId} does not have a path (objectKey) to delete from R2.`);
      }
      
      // Delete the document from Firestore
      await deleteDoc(mediaRef);
      console.log('Media document deleted from Firestore:', mediaId);
    } catch (error) {
      console.error('Error deleting media:', error);
      throw error;
    }
  },

  // Site Management
  getAllSites: async () => {
    try {
      const sitesRef = collection(db, 'sites');
      const snapshot = await getDocs(sitesRef);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error getting all sites:', error);
      throw error;
    }
  },

  getSite: async (siteId) => {
    try {
      const siteRef = doc(db, 'sites', siteId);
      const siteDoc = await getDoc(siteRef);
      if (siteDoc.exists()) {
        return {
          id: siteDoc.id,
          ...siteDoc.data()
        };
      }
      return null;
    } catch (error) {
      console.error('Error getting site:', error);
      return null;
    }
  },

  updateSite: async (siteId, updates) => {
    try {
      const siteRef = doc(db, 'sites', siteId);
      await updateDoc(siteRef, {
        ...updates,
        updatedAt: new Date()
      });
    } catch (error) {
      console.error('Error updating site:', error);
      throw error;
    }
  },

  deleteSite: async (siteId) => {
    try {
      const siteRef = doc(db, 'sites', siteId);
      await deleteDoc(siteRef);
    } catch (error) {
      console.error('Error deleting site:', error);
      throw error;
    }
  },

  // User Management
  getAllUsers: async () => {
    try {
      const usersRef = collection(db, 'users');
      const snapshot = await getDocs(usersRef);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error getting all users:', error);
      throw error;
    }
  },

  updateUser: async (userId, updates) => {
    try {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        ...updates,
        updatedAt: new Date()
      });
    } catch (error) {
      console.error('Error updating user:', error);
      throw error;
    }
  },

  deleteUser: async (userId) => {
    try {
      const userRef = doc(db, 'users', userId);
      await deleteDoc(userRef);
    } catch (error) {
      console.error('Error deleting user:', error);
      throw error;
    }
  },

  // Comment Management
  getAllComments: async () => {
    try {
      // Get all site discussions
      const discussionsRef = collection(db, 'site_discussions');
      const discussionsQuery = query(discussionsRef, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(discussionsQuery);

      // Get unique site IDs from comments
      const siteIds = [...new Set(snapshot.docs.map(doc => doc.data().siteId))];
      
      // Fetch site data for all sites in parallel
      const siteData = {};
      if (siteIds.length > 0) {
        const sitePromises = siteIds.map(async (siteId) => {
          const siteDoc = await getDoc(doc(db, 'sites', siteId));
          if (siteDoc.exists()) {
            siteData[siteId] = {
              id: siteDoc.id,
              ...siteDoc.data()
            };
          }
        });
        await Promise.all(sitePromises);
      }

      // Map comments with site data
      return snapshot.docs.map(doc => {
        const data = doc.data();
        const site = siteData[data.siteId] || {};

        // Convert Firestore timestamps to dates
        const createdAt = data.createdAt?.toDate?.() || new Date(data.createdAt);
        const updatedAt = data.updatedAt?.toDate?.() || 
          (data.updatedAt ? new Date(data.updatedAt) : null);

        return {
          id: doc.id,
          ...data,
          createdAt,
          updatedAt,
          siteName: site.name || 'Unknown Site',
          siteSlug: data.siteId,
          parentComment: data.replyTo ? {
            id: data.replyTo,
            text: 'Loading...'
          } : null
        };
      });
    } catch (error) {
      console.error('Error fetching comments:', error);
      throw error;
    }
  },

  updateComment: async (commentId, updates) => {
    try {
      const commentRef = doc(db, 'site_discussions', commentId);
      await updateDoc(commentRef, {
        ...updates,
        updatedAt: new Date()
      });
    } catch (error) {
      console.error('Error updating comment:', error);
      throw error;
    }
  },

  deleteComment: async (commentId) => {
    try {
      const commentRef = doc(db, 'site_discussions', commentId);
      const commentDoc = await getDoc(commentRef);
      
      if (!commentDoc.exists()) {
        throw new Error('Comment not found');
      }

      // Delete any replies to this comment
      const repliesQuery = query(
        collection(db, 'site_discussions'), 
        where('replyTo', '==', commentId)
      );
      const repliesSnapshot = await getDocs(repliesQuery);
      
      const deletePromises = repliesSnapshot.docs.map(replyDoc => 
        deleteDoc(doc(db, 'site_discussions', replyDoc.id))
      );
      deletePromises.push(deleteDoc(commentRef));

      await Promise.all(deletePromises);
    } catch (error) {
      console.error('Error deleting comment:', error);
      throw error;
    }
  },

  // Feature Management
  getAllFeatures: async () => {
    try {
      const featuresRef = collection(db, 'features');
      const snapshot = await getDocs(featuresRef);
      const features = {};
      snapshot.docs.forEach(doc => {
        features[doc.id] = {
          ...doc.data(),
          id: doc.id,
        };
      });
      return features;
    } catch (error) {
      console.error('Error getting features:', error);
      throw error;
    }
  },

  getFeature: async (featureId) => {
    try {
      const featureRef = doc(db, 'features', featureId);
      const featureDoc = await getDoc(featureRef);
      if (featureDoc.exists()) {
        return {
          id: featureDoc.id,
          ...featureDoc.data()
        };
      }
      throw new Error('Feature not found');
    } catch (error) {
      console.error('Error getting feature:', error);
      throw error;
    }
  },

  createFeature: async (featureData) => {
    try {
      const featureRef = doc(db, 'features', featureData.id);
      const featureDoc = await getDoc(featureRef);
      
      if (featureDoc.exists()) {
        throw new Error('Feature ID already exists');
      }

      await setDoc(featureRef, {
        label: featureData.label,
        description: featureData.description || '',
        category: featureData.category || 'other',
        autoDetected: featureData.autoDetected || false,
        active: featureData.active ?? true,
        createdAt: new Date(),
        updatedAt: new Date()
      });
    } catch (error) {
      console.error('Error creating feature:', error);
      throw error;
    }
  },

  updateFeature: async (featureId, updates) => {
    try {
      const featureRef = doc(db, 'features', featureId);
      const featureDoc = await getDoc(featureRef);
      
      if (!featureDoc.exists()) {
        throw new Error('Feature not found');
      }

      await updateDoc(featureRef, {
        ...updates,
        updatedAt: new Date()
      });
    } catch (error) {
      console.error('Error updating feature:', error);
      throw error;
    }
  },

  // Media download helpers
  getMediaDownloadUrl: (objectKey) => { // Changed from async, now expects R2 objectKey
    if (!objectKey) {
      console.error('getMediaDownloadUrl called with no objectKey.');
      // Return a specific error or empty string, consistent with client-side util
      return '';
    }
    try {
      // Uses the R2 service to construct the public URL
      return getR2PublicUrl(objectKey);
    } catch (error) {
      console.error(`Error constructing R2 public URL for key ${objectKey}:`, error);
      // Potentially re-throw or return an error indicator
      return '';
    }
  },

  // getMediaBlob: This function's logic needs re-evaluation for R2.
  // If R2 bucket is public, a direct fetch to the public URL (from getR2PublicUrl) would work.
  // If R2 bucket is private and requires signed URLs for GET, a new Worker endpoint would be needed.
  // For now, commenting out and noting that it needs a different approach.
  // The admin panel might need to fetch the blob differently or this function adapted.
  /*
  getMediaBlob: async (objectKey) => {
    try {
      const publicUrl = getR2PublicUrl(objectKey);
      if (!publicUrl) {
        throw new Error(`Could not get public URL for R2 object key: ${objectKey}`);
      }
      
      // Assuming public R2 bucket, direct fetch
      const response = await fetch(publicUrl);
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to fetch R2 object ${objectKey}: ${response.status} ${errorText}`);
      }
      return await response.blob();
    } catch (error) {
      console.error(`Error downloading blob for R2 object ${objectKey}:`, error);
      throw error;
    }
  },
  */
  // NOTE: getMediaBlob functionality needs to be re-evaluated for R2.
  // If direct public access is not desired, a new worker endpoint for authenticated GETs might be needed.

  // downloadMedia: This function relied on the old getMediaDownloadUrl and getMediaBlob.
  // It will also need re-evaluation based on how getMediaBlob is handled for R2.
  // Commenting out for now.
  /*
  downloadMedia: async (objectKey) => { // Assuming objectKey is passed now
    let attempts = 0;
    const maxAttempts = 3;
    let lastError;

    while (attempts < maxAttempts) {
      try {
        // This would use the (potentially new) getMediaBlob for R2
        // const blob = await adminFunctions.getMediaBlob(objectKey);
        // return blob;
        throw new Error("downloadMedia for R2 is not yet implemented pending getMediaBlob review.");
      } catch (error) {
        lastError = error;
        attempts++;
        if (attempts < maxAttempts) {
          await new Promise(resolve => setTimeout(resolve, 1000 * Math.pow(2, attempts)));
        }
      }
    }
    throw lastError || new Error('Max retry attempts reached for downloadMedia');
  },
  */
  // NOTE: downloadMedia functionality needs to be re-evaluated.

  deleteFeature: async (featureId) => {
    try {
      const featureRef = doc(db, 'features', featureId);
      const featureDoc = await getDoc(featureRef);
      
      if (!featureDoc.exists()) {
        throw new Error('Feature not found');
      }

      // Check if feature is in use
      const sitesRef = collection(db, 'sites');
      const sitesWithFeature = await getDocs(
        query(sitesRef, where(`features.${featureId}.enabled`, '==', true))
      );

      if (!sitesWithFeature.empty) {
        throw new Error('Cannot delete feature that is in use by sites');
      }

      await deleteDoc(featureRef);
    } catch (error) {
      console.error('Error deleting feature:', error);
      throw error;
    }
  },

  // Auto-detect Mapping Management
  getAutoDetectMappings: async () => {
    try {
      const mappingRef = doc(db, 'settings', 'autoDetectMapping');
      const mappingDoc = await getDoc(mappingRef);
      if (mappingDoc.exists()) {
        return mappingDoc.data().mappings;
      }
      return {};
    } catch (error) {
      console.error('Error getting auto-detect mappings:', error);
      throw error;
    }
  },

  createAutoDetectMapping: async (roboflowClass, featureId) => {
    try {
      const mappingRef = doc(db, 'settings', 'autoDetectMapping');
      const mappingDoc = await getDoc(mappingRef);
      
      const mappings = mappingDoc.exists() ? mappingDoc.data().mappings : {};
      
      if (mappings[roboflowClass]) {
        throw new Error('Mapping already exists for this class');
      }

      mappings[roboflowClass] = featureId;

      await setDoc(mappingRef, { 
        mappings,
        updatedAt: new Date()
      }, { merge: true });

    } catch (error) {
      console.error('Error creating auto-detect mapping:', error);
      throw error;
    }
  },

  updateAutoDetectMapping: async (roboflowClass, newFeatureId) => {
    try {
      const mappingRef = doc(db, 'settings', 'autoDetectMapping');
      const mappingDoc = await getDoc(mappingRef);
      
      if (!mappingDoc.exists()) {
        throw new Error('No mappings found');
      }

      const mappings = mappingDoc.data().mappings;
      
      if (!mappings[roboflowClass]) {
        throw new Error('Mapping not found for this class');
      }

      mappings[roboflowClass] = newFeatureId;

      await setDoc(mappingRef, { 
        mappings,
        updatedAt: new Date()
      }, { merge: true });

    } catch (error) {
      console.error('Error updating auto-detect mapping:', error);
      throw error;
    }
  },

  deleteAutoDetectMapping: async (roboflowClass) => {
    try {
      const mappingRef = doc(db, 'settings', 'autoDetectMapping');
      const mappingDoc = await getDoc(mappingRef);
      
      if (!mappingDoc.exists()) {
        throw new Error('No mappings found');
      }

      const mappings = mappingDoc.data().mappings;
      
      if (!mappings[roboflowClass]) {
        throw new Error('Mapping not found for this class');
      }

      delete mappings[roboflowClass];

      await setDoc(mappingRef, { 
        mappings,
        updatedAt: new Date()
      }, { merge: true });

    } catch (error) {
      console.error('Error deleting auto-detect mapping:', error);
      throw error;
    }
  }
};
